/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { getAllReferralCodes } from "../../api/admin";
import Card from "../../components/shared/Card";
import DateRangeFilter from "../../components/shared/DateRangeFilter";
import Swal from "sweetalert2";
import { PuffLoader } from "react-spinners";
import Button from "../../components/shared/Button";
import { BsEyeFill } from "react-icons/bs";
import { FaSearch } from "react-icons/fa";

const AllReferralCodes = () => {
  const [referralCodes, setReferralCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(10);
  const [showDetailsPopup, setShowDetailsPopup] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);

  // Estado para controlar si se muestra el total histórico o se filtran por fechas
  const [showAllRecords, setShowAllRecords] = useState(false);

  // Estados para el buscador
  const [searchTerm, setSearchTerm] = useState("");
  const [currentSearchTerm, setCurrentSearchTerm] = useState("");

  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    fromRecord: 0,
    toRecord: 0,
    totalRecords: 0,
  });

  // Estado para el filtro de fechas
  const getFirstDayOfCurrentMonth = () => {
    const today = new Date();
    return new Date(
      today.getFullYear(),
      today.getMonth(),
      1
    ).toLocaleDateString("en-CA");
  };

  const getCurrentDay = () => {
    return new Date().toLocaleDateString("en-CA");
  };

  const [dateRange, setDateRange] = useState({
    startDate: getFirstDayOfCurrentMonth(),
    endDate: getCurrentDay(),
  });

  const formatDateWithTimezone = (dateString) => {
    const date = new Date(dateString + "T00:00:00");
    return date.toISOString();
  };

  const loadReferralCodes = async (page = 1) => {
    try {
      setLoading(true);

      let apiParams = {
        page,
        limit: pageSize,
      };

      if (!showAllRecords) {
        apiParams.startDate = formatDateWithTimezone(dateRange.startDate);
        apiParams.endDate = formatDateWithTimezone(dateRange.endDate);
      }

      const response = await getAllReferralCodes(
        apiParams.page,
        apiParams.limit,
        currentSearchTerm || null,
        apiParams.startDate,
        apiParams.endDate
      );

      setReferralCodes(response.data || []);

      if (response.meta) {
        setPagination({
          currentPage: response.meta.current_page || 1,
          lastPage: response.meta.last_page || 1,
          fromRecord: response.meta.from_record || 0,
          toRecord: response.meta.to_record || 0,
          totalRecords: response.meta.total_records || 0,
        });
      } else {
        setPagination({
          currentPage: 1,
          lastPage: 1,
          fromRecord: referralCodes.length > 0 ? 1 : 0,
          toRecord: referralCodes.length,
          totalRecords: referralCodes.length,
        });
      }
    } catch (error) {
      console.error("Error loading referral codes:", error);
      setReferralCodes([]);
      setPagination({
        currentPage: 1,
        lastPage: 1,
        fromRecord: 0,
        toRecord: 0,
        totalRecords: 0,
      });
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error || "No se pudieron cargar los códigos de referido.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReferralCodes(1);
  }, []);

  useEffect(() => {
    if (!loading) {
      loadReferralCodes(1);
    }
  }, [pageSize]);

  // Efecto para recargar datos cuando cambia el filtro de fechas
  useEffect(() => {
    if (!showAllRecords) {
      loadReferralCodes(1);
    }
  }, [dateRange]);

  // Efecto separado para manejar cuando se cambia entre filtrado y todos los registros
  useEffect(() => {
    loadReferralCodes(1);
  }, [showAllRecords]);

  // Efecto para recargar datos cuando cambia el término de búsqueda
  useEffect(() => {
    loadReferralCodes(1);
  }, [currentSearchTerm]);

  const handleSearchInputChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSearch = () => {
    setCurrentSearchTerm(searchTerm.trim());
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Función para manejar cambios en el rango de fechas
  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
    if (showAllRecords) {
      setShowAllRecords(false);
    }
  };

  const toggleShowAllRecords = () => {
    setShowAllRecords(!showAllRecords);
  };

  // Función para cambiar de página
  const handlePageChange = (page) => {
    if (page < 1 || page > pagination.lastPage || loading) return;
    loadReferralCodes(page);
  };

  // Función para cambiar el tamaño de página
  const handlePageSizeChange = (e) => {
    const newSize = parseInt(e.target.value);
    if (newSize !== pageSize) {
      setPageSize(newSize);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const handleOpenDetailsPopup = (code) => {
    setSelectedCode(code);
    setShowDetailsPopup(true);
  };

  const handleCloseDetailsPopup = () => {
    setShowDetailsPopup(false);
    setSelectedCode(null);
  };

  const renderPagination = () => {
    const pages = [];
    const { currentPage, lastPage } = pagination;

    if (lastPage >= 1) {
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(lastPage, startPage + 4);

      if (endPage - startPage < 4) {
        startPage = Math.max(1, endPage - 4);
      }

      if (startPage > 1) {
        pages.push(
          <button
            key="1"
            className={`pagination__button ${
              currentPage === 1 ? "pagination__button--active" : ""
            }`}
            onClick={() => handlePageChange(1)}
            disabled={loading}
          >
            1
          </button>
        );
        if (startPage > 2) {
          pages.push(
            <span key="start-ellipsis" className="pagination__ellipsis">
              ...
            </span>
          );
        }
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(
          <button
            key={i}
            className={`pagination__button ${
              currentPage === i ? "pagination__button--active" : ""
            }`}
            onClick={() => handlePageChange(i)}
            disabled={loading}
          >
            {i}
          </button>
        );
      }

      if (endPage < lastPage) {
        if (endPage < lastPage - 1) {
          pages.push(
            <span key="end-ellipsis" className="pagination__ellipsis">
              ...
            </span>
          );
        }
        pages.push(
          <button
            key={lastPage}
            className={`pagination__button ${
              currentPage === lastPage ? "pagination__button--active" : ""
            }`}
            onClick={() => handlePageChange(lastPage)}
            disabled={loading}
          >
            {lastPage}
          </button>
        );
      }
    }

    return (
      <div className="pagination">
        <button
          className="pagination__button pagination__button--nav"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1 || loading}
        >
          &laquo;
        </button>
        {pages}
        <button
          className="pagination__button pagination__button--nav"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === lastPage || loading}
        >
          &raquo;
        </button>
      </div>
    );
  };

  const renderPageSizeSelector = () => {
    return (
      <div className="pagination__size-selector">
        <label htmlFor="pageSize" className="pagination__size-label">
          Mostrar:
        </label>
        <select
          id="pageSize"
          className="filter-select pagination__size-select"
          value={pageSize}
          onChange={handlePageSizeChange}
          disabled={loading}
        >
          <option value="5">5</option>
          <option value="10">10</option>
          <option value="20">20</option>
          <option value="50">50</option>
        </select>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="loader-container">
        <PuffLoader
          color="#009ee3"
          loading={true}
          size={60}
          margin={2}
          speedMultiplier={4}
        />
      </div>
    );
  }

  return (
    <Card className={"all-referral-codes-card"}>
      <div className="all-referral-codes">
        <div className="all-referral-codes__header">
          <h2 className="all-referral-codes__title">
            Listado de Códigos de Referido
          </h2>
          <div className="all-referral-codes__filters">
            <div className="date-filter-container">
              <div className="date-filter-controls">
                <DateRangeFilter
                  dateRange={dateRange}
                  onDateChange={handleDateRangeChange}
                />
                <Button
                  onClick={toggleShowAllRecords}
                  variant={showAllRecords ? "outline" : "primary"}
                  className="toggle-records-btn"
                >
                  {showAllRecords
                    ? "Filtrar por Período"
                    : "Ver Todo el Histórico"}
                </Button>
                <div className="input-group__search-container">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchInputChange}
                    onKeyDown={handleSearchKeyDown}
                    className="input-group__input-search"
                    placeholder="Buscar por código"
                  />
                  <button
                    className="input-group__search-button"
                    onClick={handleSearch}
                  >
                    <FaSearch />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="all-referrals__filters">{renderPageSizeSelector()}</div>
        <div className="filter-status">
          {showAllRecords ? (
            <div className="filter-info filter-info--all">
              <i className="bx bx-history"></i>
              <span>
                Mostrando todos los datos registrados (histórico completo)
              </span>
            </div>
          ) : (
            <div className="filter-info filter-info--filtered">
              <i className="bx bx-calendar"></i>
              <span>
                Mostrando datos registrados del {dateRange.startDate} al{" "}
                {dateRange.endDate}
              </span>
            </div>
          )}
        </div>

        <div className="all-referral-codes__table">
          <table>
            <thead>
              <tr>
                <th>Código</th>
                <th>Propietario</th>
                <th>Correo</th>
                <th>Referidos</th>
                <th>Detalles</th>
              </tr>
            </thead>
            <tbody>
              {referralCodes.length === 0 ? (
                <tr>
                  <td colSpan="5" className="no-data">
                    No hay códigos de referido registrados
                  </td>
                </tr>
              ) : (
                referralCodes.map((code, index) => (
                  <tr key={index}>
                    <td>{code.code || "-"}</td>
                    <td>{code.owner_name || "Sin nombre"}</td>
                    <td>{code.owner_email || "Sin correo"}</td>
                    <td>{code.registrations}</td>
                    <td>
                      <Button
                        onClick={() => handleOpenDetailsPopup(code)}
                        variant="outline"
                      >
                        <BsEyeFill size={20} />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Componente de paginación */}
        <div className="all-referral-codes__pagination">
          <div className="pagination__container">
            {renderPagination()}
            <div className="pagination__info">
              Mostrando {pagination.fromRecord} a {pagination.toRecord} de{" "}
              {pagination.totalRecords} registros
            </div>
          </div>
        </div>
      </div>

      {/* Popup para detalles del código */}
      {showDetailsPopup && selectedCode && (
        <div className="popup-overlay">
          <div className="popup-container">
            <div className="popup-header">
              <h3>Detalles del Código de Referido</h3>
              <button className="popup-close" onClick={handleCloseDetailsPopup}>
                &times;
              </button>
            </div>
            <div className="popup-content">
              <div className="details-list">
                <div className="details-item">
                  <span className="details-label">Estado:</span>
                  <td>
                    <span
                      className={`referral-status referral-status--${
                        selectedCode.is_active ? "active" : "inactive"
                      }`}
                    >
                      {selectedCode.is_active ? "Activo" : "Inactivo"}
                    </span>
                  </td>
                </div>

                <div className="details-item">
                  <span className="details-label">Primera venta:</span>
                  <span className="details-value">
                    {selectedCode.first_payment_commission_rate || 10}%
                  </span>
                </div>

                <div className="details-item">
                  <span className="details-label">Ventas siguientes:</span>
                  <span className="details-value">
                    {selectedCode.following_payments_commission_rate || 5}%
                  </span>
                </div>

                <div className="details-item">
                  <span className="details-label">Ganancias:</span>
                  <span className="details-value">
                    {formatCurrency(selectedCode.earnings)}
                  </span>
                </div>

                <div className="details-item">
                  <span className="details-label">Fecha de creación:</span>
                  <span className="details-value">
                    {formatDate(selectedCode.created_at)}
                  </span>
                </div>
              </div>
              <div className="popup-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleCloseDetailsPopup}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default AllReferralCodes;
